import { Link } from 'react-router-dom';
import { LuChevronRight, LuX } from '@platform/icon';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useEducationCart } from '../model/education-cart.context';
import { formatEducationPrice } from '../lib/education-format';

/**
 * 선택한 회차 요약 바. 담기만 해서는 정원이 잡히지 않는다는 것을 문구로 알린다 —
 * 담아둔 자리가 보장된다고 믿으면 마감 화면에서 접수 실패를 배신으로 받아들인다.
 *
 * 화면 하단에 **고정**한다. 회차를 여러 개 고르는 화면인데 목록이 길어지면 담은
 * 결과와 다음 단계 버튼이 스크롤 밖으로 밀려나, 고를수록 확인이 어려워진다.
 * (부모의 좌우 패딩을 음수 마진으로 걷어내 화면 폭 전체를 쓴다)
 */
export function CartBar() {
  const { items, count, remove } = useEducationCart();
  if (count === 0) return null;

  const total = items.reduce((sum, i) => sum + i.price, 0);

  return (
    <aside
      aria-label="선택한 교육 회차"
      className="sticky bottom-0 z-30 -mx-4 mt-8 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:-mx-6 sm:px-6 dark:border-white/15 dark:bg-neutral-950/95 dark:supports-[backdrop-filter]:bg-neutral-950/80"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="mb-1.5 text-xs font-semibold text-gray-500 dark:text-neutral-400">
            선택한 회차 {count}개
          </p>
          <ul className="flex min-w-0 gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((item) => (
            <li key={item.sessionId} className="shrink-0">
              <button
                type="button"
                onClick={() => remove(item.sessionId)}
                className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2.5 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:bg-white/10 dark:text-neutral-200 dark:hover:bg-white/15"
                aria-label={`${item.sessionTitle} 선택 해제`}
              >
                <span className="max-w-44 truncate">{item.sessionTitle}</span>
                <LuX aria-hidden className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
          </ul>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-4 lg:justify-end">
          <p className="text-xs text-gray-500 dark:text-neutral-400">
            1인 기준 합계
            <span className="ml-2 text-base font-bold tabular-nums text-gray-950 dark:text-neutral-100">
              {formatEducationPrice(total)}
            </span>
          </p>
          <Link
            to={PATHS.EDUCATION_CHECKOUT}
            viewTransition
            className="inline-flex h-11 items-center gap-1 rounded-lg bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          >
            신청서 작성
            <LuChevronRight aria-hidden className="h-4 w-4" />
          </Link>
        </div>
      </div>
      <p className="mt-1.5 text-[11px] leading-4 text-gray-400 dark:text-neutral-500">
        선택만으로는 자리가 잡히지 않습니다. 접수를 마쳐야 정원이 확보됩니다.
      </p>
    </aside>
  );
}
